import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, Badge, HStack, VStack, Flex, Spinner, useDisclosure,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import api from '../api';
import { PlannedVisitModal } from './SiteVisitorsDashboard';

const STATUS_COLORS = {
  scheduled: 'gray', confirmed: 'blue', in_progress: 'orange',
  completed: 'green', cancelled: 'red', no_show: 'red',
};

const fmtDt = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';

export default function PlannedVisitsTaskModal({ taskId, taskTitle, projectId, isOpen, onClose }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [premises, setPremises] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const addDisclosure = useDisclosure();

  const fetchVisits = () => {
    if (!taskId) return;
    setLoading(true);
    api.get(`/visitors/authorised/by-task/${taskId}`)
      .then(r => setVisits(r.data || []))
      .catch(() => setVisits([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOpen || !taskId) return;
    fetchVisits();
    // Fetch premises and users for the Add form
    api.get('/visitors/premises/list').then(r => setPremises(r.data || [])).catch(() => {});
    api.get('/calendar/users').then(r => setAllUsers(r.data || [])).catch(() => {});
  }, [isOpen, taskId]); // eslint-disable-line

  // Build a pre-populated visit object for the modal
  const prefillVisit = {
    task_id: taskId,
    project_id: projectId || '',
    title: taskTitle ? `Visit — ${taskTitle}` : '',
    // Leave other fields blank for user to fill in
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">
            <HStack>
              <Text>Planned Visits</Text>
              {taskTitle && <Text fontSize="12px" color="gray.400" fontWeight="400">— {taskTitle}</Text>}
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loading ? (
              <Flex justify="center" py={6}><Spinner /></Flex>
            ) : visits.length === 0 ? (
              <Text fontSize="sm" color="gray.400" textAlign="center" py={8}>
                No planned visits for this task.
              </Text>
            ) : (
              <VStack spacing={2} align="stretch">
                {visits.map(v => (
                  <Box key={v.id} p={3} border="1px" borderColor="gray.200" borderRadius="md">
                    <Flex justify="space-between" align="flex-start">
                      <Box flex={1}>
                        <HStack spacing={2} mb={1}>
                          <Badge colorScheme={STATUS_COLORS[v.status] || 'gray'} fontSize="9px" textTransform="capitalize">
                            {(v.status || '').replace('_', ' ')}
                          </Badge>
                          <Text fontSize="13px" fontWeight="600">{v.title}</Text>
                        </HStack>
                        <HStack spacing={3} fontSize="11px" color="gray.500" flexWrap="wrap">
                          <Text>📅 {fmtDt(v.expected_at)}</Text>
                          {v.host_name && <Text>👤 {v.host_name}</Text>}
                          {v.company_name && <Text>🏢 {v.company_name}</Text>}
                          {v.premises_name && <Text>📍 {v.premises_name}</Text>}
                        </HStack>
                        {v.visit_reason && <Text fontSize="11px" color="gray.400" mt={1} noOfLines={2}>{v.visit_reason}</Text>}
                      </Box>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size="sm" leftIcon={<FiPlus />} colorScheme="orange" variant="outline" mr="auto"
              onClick={addDisclosure.onOpen}>
              + Add Planned Visit
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Planned Visit modal — pre-populated with task */}
      <PlannedVisitModal
        isOpen={addDisclosure.isOpen}
        onClose={addDisclosure.onClose}
        onSaved={() => { addDisclosure.onClose(); fetchVisits(); }}
        premisesList={premises}
        allUsers={allUsers}
        editVisit={null}
        prefillVisit={prefillVisit}
      />
    </>
  );
}
